import React from 'react';
import { Receipt, OrderDetailsDTO } from '../../types/order';

interface ReceiptPrintViewProps {
  receipt: Receipt;
  order: OrderDetailsDTO;
}

const ReceiptPrintView: React.FC<ReceiptPrintViewProps> = ({ receipt, order }) => {
  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return '₹0.00';
    return `₹${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPaymentMethod = (method: string) => {
    const methodMap: Record<string, string> = {
      cash: 'Cash',
      credit_card: 'Credit Card',
      debit_card: 'Debit Card',
      upi: 'UPI',
      razorpay: 'Razorpay',
      stripe: 'Stripe',
      cheque: 'Cheque',
      bank_transfer: 'Bank Transfer',
      simulated: 'Simulated',
    };
    return methodMap[method.toLowerCase()] || method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Calculate totals
  const invoiceTotal = order.price?.total || 0;
  const totalPaid = (order.receipts || []).reduce((sum, r) => sum + r.amount, 0);
  const remainingBalance = receipt.remainingBalance;

  return (
    <div className="print-container p-8 max-w-4xl mx-auto bg-white">
      <style>{`
        @media print {
          @page {
            margin: 1cm;
          }
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
        .print-container {
          font-family: Arial, sans-serif;
          color: #000;
        }
      `}</style>

      {/* Header with Master Ji Branding */}
      <div className="mb-8 pb-6 border-b-2 border-gray-800">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Master Ji</h1>
            <p className="text-sm text-gray-600">Digital Tailoring Marketplace</p>
            <p className="text-xs text-gray-500 mt-1">Receipt Number: {receipt.receiptNumber}</p>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">RECEIPT</h2>
            <p className="text-sm text-gray-600">Date: {formatDate(receipt.receiptDate)}</p>
            <p className="text-sm text-gray-600">Order: #{order.id}</p>
          </div>
        </div>
      </div>

      {/* Customer & Tailor Info */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Received From:</h3>
          <p className="text-sm text-gray-700 font-medium">{order.customer.name}</p>
          {order.customer.phone && (
            <p className="text-sm text-gray-700">{order.customer.phone}</p>
          )}
        </div>
        {order.assignedTailor && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Tailor:</h3>
            <p className="text-sm text-gray-700">{order.assignedTailor.name}</p>
          </div>
        )}
      </div>

      {/* Payment Details */}
      <div className="mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">Payment Details</h3>
        <div className="bg-gray-50 p-4 rounded">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Payment Amount:</span>
              <span className="text-lg font-bold text-gray-900">{formatCurrency(receipt.amount)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-300">
              <span className="text-sm text-gray-600">Payment Method:</span>
              <span className="text-sm font-medium text-gray-900">{formatPaymentMethod(receipt.paymentMethod)}</span>
            </div>
            {receipt.transactionId && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Transaction ID:</span>
                <span className="text-sm font-mono text-gray-700">{receipt.transactionId}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Payment Date:</span>
              <span className="text-sm text-gray-700">{formatDate(receipt.paymentDate)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* GST/Tax Breakdown */}
      {receipt.gstAmount && receipt.gstAmount > 0 && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Tax Breakdown</h3>
          <div className="bg-gray-50 p-4 rounded">
            <div className="space-y-2">
              {receipt.igstAmount ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span>IGST ({receipt.gstPercentage}%):</span>
                    <span>{formatCurrency(receipt.igstAmount)}</span>
                  </div>
                </>
              ) : (
                <>
                  {receipt.cgstAmount && (
                    <div className="flex justify-between text-sm">
                      <span>CGST ({receipt.gstPercentage ? (receipt.gstPercentage / 2).toFixed(2) : '0'}%):</span>
                      <span>{formatCurrency(receipt.cgstAmount)}</span>
                    </div>
                  )}
                  {receipt.sgstAmount && (
                    <div className="flex justify-between text-sm">
                      <span>SGST ({receipt.gstPercentage ? (receipt.gstPercentage / 2).toFixed(2) : '0'}%):</span>
                      <span>{formatCurrency(receipt.sgstAmount)}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-between text-sm pt-2 border-t border-gray-300 font-medium">
                <span>Total Tax:</span>
                <span>{formatCurrency(receipt.gstAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Summary */}
      <div className="mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">Balance Summary</h3>
        <div className="bg-gray-50 p-4 rounded">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Invoice Total:</span>
              <span>{formatCurrency(invoiceTotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Amount Paid:</span>
              <span>{formatCurrency(totalPaid)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300 mt-2">
              <span>Remaining Balance:</span>
              <span className={remainingBalance > 0 ? 'text-red-600' : 'text-green-600'}>
                {formatCurrency(remainingBalance)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items Summary */}
      {order.items && order.items.length > 0 && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Order Items</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Item</th>
                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Description</th>
                <th className="border border-gray-300 px-4 py-2 text-right text-sm font-semibold">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={item.id || index}>
                  <td className="border border-gray-300 px-4 py-2 text-sm">{item.garmentType}</td>
                  <td className="border border-gray-300 px-4 py-2 text-sm">
                    {item.fabric && `${item.fabric}`}
                    {item.clothType && item.clothType !== item.fabric && ` - ${item.clothType}`}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-right text-sm">{item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      {receipt.notes && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
          <p className="text-sm text-gray-700">{receipt.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-gray-300 text-center text-xs text-gray-500">
        <p className="font-semibold mb-2">Thank you for choosing Master Ji!</p>
        <p>This is a computer-generated receipt.</p>
        <p className="mt-2">For any queries, please contact us at infotherealmasterji@gmail.com</p>
      </div>
    </div>
  );
};

export default ReceiptPrintView;

